import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material';
import { ActivatedRoute } from '@angular/router';
import { interval, Subscription, timer } from 'rxjs';
import { switchMap, take, takeUntil, takeWhile } from 'rxjs/operators';
import { CanisterTypeHttpService } from '../../../../core/settings/_services/canister-type-http.service';
import { RegisterMachineHttpService } from '../../../../core/register-machine/_services/register-machine-http.service';
import {
	CANISTER_TYPES,
	CanisterTypeName,
	CanisterTypeRequestError,
	CanisterTypeState,
	canisterStateLabel,
	canisterTypeErrorMessage,
	canSendCanisterType,
	currentTypeText,
	displayCanisterType,
	isApplied,
	isAwaitingMachine,
	isCanisterTypeName,
	machineTypeSource,
	MachineTypeSource,
	notConfirmedDetail,
	registeredCanisterType,
	waitingDetail
} from '../../../../core/settings/_services/canister-type.util';
import { MixpanelService } from '../../../../services/mixpanel.service';
import { MachineRouteService } from '../machine-route.service';
import { CanisterTypeConfirmDialogComponent } from './canister-type-confirm-dialog.component';

type CanisterTypePhase = 'idle' | 'sending' | 'waiting' | 'applied' | 'not_confirmed' | 'failed';

@Component({
	selector: 'kt-canister-type',
	templateUrl: './canister-type.component.html',
	styleUrls: ['./canister-type.component.scss']
})
export class CanisterTypeComponent implements OnInit, OnDestroy {
	readonly typeOptions = CANISTER_TYPES.map(type => ({ label: type, value: type }));

	serialNo = '';
	machineName = '';
	state: CanisterTypeState = null;
	// From the registration record. Shown only when the machine has not reported a type.
	registered: CanisterTypeName | null = null;
	selection: CanisterTypeName | null = null;
	isLoading = false;
	loadError = '';
	phase: CanisterTypePhase = 'idle';
	statusMessage = '';

	private destroyed = false;
	private selectionSet = false;
	private pollSubscription: Subscription = null;

	constructor(
		private route: ActivatedRoute,
		private machineRouteService: MachineRouteService,
		private http: CanisterTypeHttpService,
		private registrationHttp: RegisterMachineHttpService,
		private dialog: MatDialog,
		private cdr: ChangeDetectorRef,
		private mixpanelService: MixpanelService
	) {}

	ngOnInit(): void {
		const machine = this.machineRouteService.getMachine(this.route);
		if (!machine) {
			this.loadError = 'Open this page from a machine.';
			return;
		}
		this.serialNo = machine.serial_no ? String(machine.serial_no) : '';
		this.machineName = machine.machine_name ? String(machine.machine_name) : '';
		this.mixpanelService.track(this.mixpanelService.PAGE_VIEW, {
			machine_id: machine.machine_id,
			machine_name: machine.machine_name,
			machine_serial_no: this.serialNo
		});
		if (!this.serialNo) {
			this.loadError = 'This machine has no serial number, so its canister type cannot be read or changed from here.';
			return;
		}
		this.loadState(false);
		this.loadRegistered();
	}

	ngOnDestroy(): void {
		this.destroyed = true;
		this.stopWatching();
	}

	get formLocked(): boolean {
		return this.phase === 'sending';
	}

	get canSend(): boolean {
		return canSendCanisterType(this.state, this.selection, this.formLocked);
	}

	// The selection is the type already requested, so there is nothing to send.
	get selectionIsRequested(): boolean {
		return !!this.state && !!this.selection && this.selection === this.state.desired;
	}

	get switchLabel(): string {
		return this.selection ? 'Switch to ' + this.selection : 'Switch';
	}

	get waitingTitle(): string {
		return 'Waiting for the machine to apply ' + displayCanisterType(this.state ? this.state.desired : null);
	}

	get waitingText(): string {
		return waitingDetail(this.state ? this.state.reported : null);
	}

	get notConfirmedText(): string {
		return notConfirmedDetail(this.state ? this.state.desired : null, this.state ? this.state.reported : null);
	}

	get machineSource(): MachineTypeSource {
		return machineTypeSource(this.state ? this.state.reported : null, this.registered);
	}

	hasType(value: string | null): boolean {
		return isCanisterTypeName(value);
	}

	typeLabel(value: string | null): string {
		return displayCanisterType(value);
	}

	stateLabel(): string {
		return this.state ? canisterStateLabel(this.state) : '';
	}

	onSelectionChange(value: CanisterTypeName): void {
		this.selection = value;
		this.selectionSet = true;
		if (this.phase === 'failed' || this.phase === 'applied') {
			this.phase = this.state && isAwaitingMachine(this.state) ? 'waiting' : 'idle';
			this.statusMessage = '';
		}
	}

	onRefresh(): void {
		if (this.formLocked || !this.serialNo) {
			return;
		}
		this.loadState(false);
	}

	// After polling gives up. Looks again and, if still pending, watches for another minute.
	onCheckAgain(): void {
		this.onRefresh();
	}

	onSwitch(): void {
		if (!this.canSend || !this.state || !this.selection) {
			return;
		}
		const nextType = this.selection;
		const dialogRef = this.dialog.open(CanisterTypeConfirmDialogComponent, {
			width: '480px',
			data: {
				serialNo: this.serialNo,
				currentText: currentTypeText(this.state.reported, this.registered),
				nextType
			}
		});
		dialogRef.afterClosed().subscribe(result => {
			if (this.destroyed || !result || !result.confirmed) {
				return;
			}
			this.send(nextType);
		});
	}

	private send(canisterType: CanisterTypeName): void {
		this.stopWatching();
		this.phase = 'sending';
		this.statusMessage = 'Sending…';
		this.cdr.detectChanges();
		this.http.setCanisterType(this.serialNo, canisterType).subscribe({
			next: () => {
				if (this.destroyed) {
					return;
				}
				this.loadState(true);
			},
			error: (error: CanisterTypeRequestError) => {
				if (this.destroyed) {
					return;
				}
				this.phase = 'failed';
				this.statusMessage = canisterTypeErrorMessage(error, 'change');
				this.cdr.detectChanges();
			}
		});
	}

	// The card still works without it: it shows Unknown when nothing is reported.
	private loadRegistered(): void {
		this.registrationHttp.getRegistration(this.serialNo).pipe(take(1)).subscribe({
			next: rows => {
				if (this.destroyed) {
					return;
				}
				// Not preselected: switching to the type it is only assumed to have should be a deliberate pick.
				this.registered = registeredCanisterType(rows);
				this.cdr.detectChanges();
			},
			error: () => {}
		});
	}

	private loadState(afterSend: boolean): void {
		if (!afterSend) {
			this.isLoading = !this.state;
			this.loadError = '';
		}
		this.http.getCanisterType(this.serialNo).subscribe({
			next: nextState => {
				if (this.destroyed) {
					return;
				}
				this.state = nextState;
				this.isLoading = false;
				this.loadError = '';
				if (!this.selectionSet) {
					this.selection = this.initialSelection(nextState);
					this.selectionSet = true;
				}
				this.applyLoadedState(afterSend);
				this.cdr.detectChanges();
			},
			error: (error: CanisterTypeRequestError) => {
				if (this.destroyed) {
					return;
				}
				this.isLoading = false;
				if (afterSend) {
					this.phase = 'failed';
					this.statusMessage = 'The request was sent, but the cloud could not be checked afterward. Refresh before trying again.';
				} else if (!this.state) {
					this.loadError = canisterTypeErrorMessage(error, 'load');
				} else {
					this.phase = 'failed';
					this.statusMessage = canisterTypeErrorMessage(error, 'load');
				}
				this.cdr.detectChanges();
			}
		});
	}

	private applyLoadedState(afterSend: boolean): void {
		if (isAwaitingMachine(this.state)) {
			this.phase = 'waiting';
			this.statusMessage = '';
			this.watchUntilSettled();
			return;
		}
		this.stopWatching();
		if (isApplied(this.state) && afterSend) {
			this.phase = 'applied';
			this.statusMessage = 'The machine reported ' + displayCanisterType(this.state.reported) + '.';
			return;
		}
		this.phase = 'idle';
		this.statusMessage = '';
	}

	private watchUntilSettled(): void {
		this.stopWatching();
		this.pollSubscription = interval(3000).pipe(
			takeUntil(timer(60000)),
			switchMap(() => this.http.getCanisterType(this.serialNo)),
			takeWhile((nextState: CanisterTypeState) => isAwaitingMachine(nextState), true)
		).subscribe({
			next: (nextState: CanisterTypeState) => {
				if (this.destroyed) {
					return;
				}
				this.state = nextState;
				if (!isAwaitingMachine(nextState) && isApplied(nextState)) {
					this.phase = 'applied';
					this.statusMessage = 'The machine reported ' + displayCanisterType(nextState.reported) + '.';
					this.stopWatching();
				} else if (isAwaitingMachine(nextState)) {
					this.phase = 'waiting';
				} else {
					this.phase = 'idle';
					this.statusMessage = '';
				}
				this.cdr.detectChanges();
			},
			error: () => {
				if (this.destroyed) {
					return;
				}
				this.phase = 'failed';
				this.statusMessage = 'Could not check whether the machine applied the change. Refresh to look again.';
				this.cdr.detectChanges();
			},
			complete: () => {
				if (this.destroyed || this.phase !== 'waiting') {
					return;
				}
				this.phase = 'not_confirmed';
				this.pollSubscription = null;
				this.cdr.detectChanges();
			}
		});
	}

	private initialSelection(nextState: CanisterTypeState): CanisterTypeName | null {
		if (isCanisterTypeName(nextState.desired)) {
			return nextState.desired;
		}
		if (isCanisterTypeName(nextState.reported)) {
			return nextState.reported;
		}
		return null;
	}

	private stopWatching(): void {
		if (this.pollSubscription) {
			this.pollSubscription.unsubscribe();
			this.pollSubscription = null;
		}
	}
}
